import { useState } from "react";
import {
  DeleteOutlined,
  LeftCircleOutlined,
  AimOutlined,
  LoadingOutlined
} from "@ant-design/icons";
import {
  Button,
  Col,
  Descriptions,
  Row,
  Table,
  Tag,
  notification,
  Popconfirm,
  Typography
} from "antd";
import { ColumnsType } from "antd/es/table";
import {
  useAddLocationToPartnerMutation,
  useGetPartnerQuery,
  useRemoveLocationFromPartnerMutation
} from "../../redux/services/partnerApi";
import ErrorDetails from "../../components/ErrorDetails";
import { useNavigate, useParams } from "react-router-dom";
import AddLocationInPartnerModal from "./AddLocationInPartnerModal";

const { Text } = Typography;

const ViewPartner = (props: any) => {
  const params = useParams();
  const navigate = useNavigate();

  const partnerId = params?.partnerId;

  const [loading, setLoading] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState({});
  const [showModal, setShowModal] = useState(false);

  const { data, error, isLoading, refetch } = useGetPartnerQuery(partnerId);
  const partner = data?.partner;
  const [removeLocationFromPartner] = useRemoveLocationFromPartnerMutation();

  const [api, contextHolder] = notification.useNotification();

  const onGoBack = () => {
    navigate(-1);
  };

  const onRemove = async (location: any) => {
    const locationId = location?.id;
    setSelectedLocationId(locationId);

    try {
      setLoading(true);
      const payload = await removeLocationFromPartner({ partnerId, locationId }).unwrap();
      api.success({
        message: "Delete operation successful",
        placement: "top"
      });
      refetch();
      setLoading(false);
    } catch (error: any) {
      api.info({
        message: "Delete operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onShowAddLocationModal = (location: any) => {
    setShowModal(true);
    setSelectedPartner(location);
  };

  const columns: ColumnsType<any> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code"
    },
    {
      title: "Location Type",
      dataIndex: "location_type",
      key: "location_type",
      render: (location_type) => <Text>{location_type ? location_type?.toUpperCase() : "-"}</Text>
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (tag) => (
        <Tag color={tag === "active" ? "green" : "red"} key={tag}>
          {tag.toUpperCase()}
        </Tag>
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_, location) => (
        <Popconfirm
          title="Are you sure you want to delete this location?"
          okText="Delete"
          placement="bottom"
          cancelText="Cancel"
          onConfirm={(event: any) => {
            onRemove(location);
          }}
          onCancel={(event: any) => event.stopPropagation()}
        >
          <Button
            icon={<DeleteOutlined />}
            danger
            loading={selectedLocationId === location.id ? loading : false}
            title="Remove"
          ></Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <>
      {contextHolder}
      {!isLoading && data && (
        <>
          <Row style={{ marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>Partner Details</h2>
              <Button onClick={onGoBack} size="middle" type="default" icon={<LeftCircleOutlined />}>
                Back
              </Button>
            </Col>
          </Row>

          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 300 }}
            size="middle"
          >
            <Descriptions.Item label="Name">{data?.partner.name}</Descriptions.Item>
            <Descriptions.Item label="Code">{data?.partner.code || "-"}</Descriptions.Item>
            <Descriptions.Item label="Company Name">{data?.partner.company_name}</Descriptions.Item>

            <Descriptions.Item label="Contact Person Name">
              {data?.partner.contact_person || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Contact Number">
              {data?.partner.contact_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Other Contact Number">
              {data?.partner.alt_contact_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="GST Number">{data?.partner.gst || "-"}</Descriptions.Item>
            <Descriptions.Item label="Billing Address">
              {data?.partner.billing_address || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={data?.partner.status === "active" ? "green" : "red"}>
                {data?.partner.status.toUpperCase()}
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label="Locations">
              {data?.partner?.locations?.map((location: any, index: number) => (
                <Text key={location?.location?.name}>
                  {location?.location?.name}{" "}
                  {index === data?.partner?.locations?.length - 1 ? "" : "| "}
                </Text>
              ))}
            </Descriptions.Item>
          </Descriptions>

          <Row style={{ marginTop: 20, marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>Location</h2>
              <Button
                size="middle"
                type="default"
                icon={<AimOutlined />}
                onClick={() => onShowAddLocationModal(partner)}
                title="Add Location"
              >
                Add Location
              </Button>
            </Col>
          </Row>

          <Table
            rowKey="id"
            bordered
            columns={columns}
            pagination={false}
            dataSource={data?.partner?.locations?.map((loc: any) => {
              return { ...loc?.location, id: loc?.id };
            })}
            loading={isLoading}
            scroll={{ x: 400 }}
          />
        </>
      )}
      {isLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh"
          }}
        >
          <LoadingOutlined style={{ fontSize: 24 }} spin />
        </div>
      )}

      <AddLocationInPartnerModal
        isModalOpen={showModal}
        partner={selectedPartner}
        onCancel={() => setShowModal(false)}
        refetch={refetch}
      />
    </>
  );
};

export default ViewPartner;
